"use client";

import {
  IconArrowUpRight,
  IconBuildingSkyscraper,
  IconCurrencyDollar,
  IconDotsVertical,
  IconLogin,
  IconLogout,
  IconShieldLock,
} from "@tabler/icons-react";
import { useRouter } from "next/navigation";
import { Avatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { buildLoginUrl, useAuth } from "@/lib/auth";
import { appSidebarNavAll } from "@/lib/navigation/app-sidebar-nav";

export function NavUser({
  user,
  isAuthenticated = true,
}: {
  user: {
    name: string;
    email?: string;
    avatar?: string;
  };
  isAuthenticated?: boolean;
}) {
  const { state, isMobile: _isMobile } = useSidebar();
  const router = useRouter();
  const { docsSettings, orgKind } = useAuth();
  const navItemsForMenu = appSidebarNavAll.filter((item) => {
    if (item.url === "/docs/" && docsSettings?.globally_enabled === false) {
      return false;
    }
    if (item.url === "/manage/org" && orgKind !== "team") {
      return false;
    }
    return true;
  });
  const showAdminLink = isAuthenticated && user.email?.toLowerCase().endsWith("@radium.cloud");

  const handleNavigate = (url: string) => {
    router.push(url);
  };

  const openMarketingLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSignIn = () => {
    router.push(buildLoginUrl());
  };

  const SignInAvatar = ({ className }: { className: string }) => (
    <Avatar className={className}>
      <div className="bg-muted flex size-full items-center justify-center rounded-lg">
        <IconLogin className="size-4" aria-hidden="true" />
      </div>
    </Avatar>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip="Account"
              className="rounded-none h-auto py-3 px-4 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {isAuthenticated ? (
                <Avatar
                  className="h-8 w-8 rounded-lg grayscale"
                  src={user.avatar}
                  alt={user.name}
                  name={user.name}
                  email={user.email}
                  slotProps={{ fallback: { className: "rounded-lg" } }}
                />
              ) : (
                <SignInAvatar className="h-8 w-8 rounded-lg grayscale" />
              )}
              {state !== "collapsed" && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user.name}</span>
                    {user.email ? <span className="text-muted-foreground truncate text-xs">{user.email}</span> : null}
                  </div>
                  <IconDotsVertical className="ml-auto size-4" />
                </>
              )}
              {state === "collapsed" && <span className="sr-only">Open account menu</span>}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                {isAuthenticated ? (
                  <Avatar
                    className="h-8 w-8 rounded-lg"
                    src={user.avatar}
                    alt={user.name}
                    name={user.name}
                    email={user.email}
                    slotProps={{ fallback: { className: "rounded-lg" } }}
                  />
                ) : (
                  <SignInAvatar className="h-8 w-8 rounded-lg" />
                )}
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  {user.email ? <span className="text-muted-foreground truncate text-xs">{user.email}</span> : null}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isAuthenticated ? (
              <>
                <DropdownMenuGroup>
                  {navItemsForMenu.map((item) => (
                    <DropdownMenuItem key={item.title + item.url} onClick={() => handleNavigate(item.url)}>
                      <item.icon />
                      {item.title}
                    </DropdownMenuItem>
                  ))}
                  {showAdminLink ? (
                    <DropdownMenuItem onClick={() => window.open("/admin", "_blank", "noopener,noreferrer")}>
                      <IconShieldLock />
                      Admin
                      <IconArrowUpRight className="ml-auto" aria-hidden />
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => openMarketingLink("https://radium.cloud/pricing")}>
                <IconCurrencyDollar />
                Pricing
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openMarketingLink("https://radium.cloud")}>
                <IconBuildingSkyscraper />
                About Us
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            {isAuthenticated ? (
              <DropdownMenuItem
                onClick={() => {
                  window.location.href = "/logout";
                }}
              >
                <IconLogout />
                Log out
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleSignIn}>
                <IconLogin />
                Sign in
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
